﻿import { useState, useEffect } from "react";
import { Col, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { format, validate } from "rut.js";

import { hasPermission, letterInString } from "@/services/Utils";
import { useOrderSaleCreate } from "@/hooks/purchases/orders_sale";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import PurchasePreview from "@/components/Purchase/PurchasePreview";

import classes from "./createOrderSale.module.scss";

const CreateOrderSaleButton = ({ params, base, data, setData, save, isFetchingDetail }) => {
	const navigate = useNavigate();
	const { addToast } = useToasts();

	const { data: response } = useOrderSaleCreate(params);
	const { permissions } = response;

	const { customer } = useSelector((state) => state.auth.user);

	const [missingTags, setMissingTags] = useState(false);
	const [showCreateTags, setShowCreateTags] = useState(false);
	const [showPreview, setShowPreview] = useState(false);
	const [messagesEdit, setMessagesEdit] = useState({});
	const [messagesDelete, setMessagesDelete] = useState({});

	useEffect(() => {
		const noTags = data.items.filter((i) => Number(i.generated_tags) === 1);
		if (data.items.length !== noTags.length) {
			setMissingTags(true);
		}
		// eslint-disable-next-line
	}, [data.items.length]);

	useEffect(() => {
		const validateItems = () => {
			const newMessagesEdit = {};
			const newMessagesDelete = {};

			data.items.forEach((item) => {
				const { id, tradename, quantity_base, quantity, container_content_inventories, remaining_container_content, save_as } = item;

				if (
					quantity_base &&
					quantity_base !== 0 &&
					parseFloat(quantity) !== parseFloat(quantity_base) &&
					parseFloat(container_content_inventories) === parseFloat(remaining_container_content)
				) {
					if (save_as && save_as !== "") {
						newMessagesEdit[id] =
							`Al editar la cantidad de envases del producto "${tradename}", se modificará el contenido total del producto genérico.`;
					} else {
						newMessagesEdit[id] =
							`Al editar la cantidad de envases del producto "${tradename}", se eliminarán las etiquetas y deberá imprimir nuevamente.`;
					}
				}

				if (
					quantity_base &&
					quantity_base !== 0 &&
					parseFloat(quantity) !== parseFloat(quantity_base) &&
					parseFloat(container_content_inventories) > parseFloat(remaining_container_content)
				) {
					newMessagesDelete[id] = `No se puede modificar la cantidad de envases del producto "${tradename}" ya que tiene aplicaciones asociadas.`;
				}
			});

			setMessagesEdit(newMessagesEdit);
			setMessagesDelete(newMessagesDelete);
		};

		validateItems();
	}, [data.items]);

	const validSave = () => {
		if (!data.business_id) {
			addToast("Debe seleccionar razón social para continuar", {
				appearance: "error",
			});
			return false;
		}

		if (!data.estate_id) {
			addToast(
				`Debe seleccionar ${
					customer.customer_screens.find((cs) => cs.screen_id === 51) ? customer.customer_screens.find((cs) => cs.screen_id === 51)?.name : "Campo"
				} para continuar`,
				{
					appearance: "error",
				},
			);
			return false;
		}

		if (data.rut_dealer) {
			// To-Do: validar esto para clientes extranjeros
			if (customer.country_id !== 1) {
				setData({
					...data,
					rut_dealer: "",
				});
			} else {
				if (!validate(format(data.rut_dealer))) {
					addToast("Debe ingresar un rut válido para continuar.", {
						appearance: "error",
					});
					return false;
				}
			}
		}

		if (!data.type_currency_id) {
			addToast("Debe seleccionar la moneda para continuar", {
				appearance: "error",
			});
			return false;
		}

		if (data && data.items.length === 0) {
			addToast("Debe ingresar el menos un producto para guardar", {
				appearance: "error",
			});
			return false;
		}

		let valid_item = true;
		let message = "";
		data.items.forEach((item, i) => {
			if (!item.product_id) {
				message = "Debe seleccionar un producto en todos los items";
				valid_item = false;
				return;
			}

			if (!item.quantity || Number(item.quantity) === 0) {
				message = "Debe ingresar la cantidad de envases en todos los items";
				valid_item = false;
				return;
			}

			if (!item.container_content || Number(item.container_content) === 0) {
				message = "Debe ingresar el contenido en todos los items";
				valid_item = false;
				return;
			}

			if (letterInString(".", item.container_content) > 1) {
				message = "Debe ingresar un formato válido en el campo contenido envase para el item" + (i + 1);
				valid_item = false;
				return;
			}

			if (!item.value_unit_measurement || !item.unit_measurement) {
				message = "Debe seleccionar la unidad de medida en todos los items";
				valid_item = false;
				return;
			}

			if (item.price_total) {
				if (!data.type_currency_id) {
					message = "Debe seleccionar el tipo de moneda";
					valid_item = false;
					return;
				}
			}

			if (item.quantity_lot && item.save_as) {
				message = "No es posible cargar en un producto genérico al tener distribución por lotes.";
				valid_item = false;
				return;
			}

			if (
				Object.prototype.hasOwnProperty.call(item, "save_as_original") &&
				item.save_as_original &&
				item.save_as_original !== "" &&
				Number(item.save_as_original) !== Number(item.save_as)
			) {
				message = `El producto genérico no puede ser modificado en el item ${i + 1} debido a que ya fue guardado en la guía de despacho.`;
				valid_item = false;
				return;
			}
		});

		if (!valid_item) {
			addToast(message, {
				appearance: "error",
			});
			return false;
		}

		return true;
	};

	const handleValid = () => {
		if (validSave()) {
			let onlyLot = true;
			let onlyMasive = true;
			let allDelivery = true;

			data.items.forEach((element) => {
				if (element.quantity_lot === "") {
					onlyLot = false;
				}

				if (!Object.prototype.hasOwnProperty.call(element, "rack") || !Object.prototype.hasOwnProperty.call(element, "shelf")) {
					onlyMasive = false;
				}

				if (
					Object.prototype.hasOwnProperty.call(element, "quantity_reception") &&
					element.quantity_reception &&
					Number(element.quantity_reception) < Number(element.quantity) * Number(element.container_content) * Number(element.value_unit_measurement)
				) {
					allDelivery = false;
				}

				if (!Object.prototype.hasOwnProperty.call(element, "quantity_reception") || !element.quantity_reception) {
					allDelivery = false;
				}
			});

			if (onlyLot || onlyMasive || allDelivery) {
				handleSave(true);
				return;
			}

			setShowCreateTags(true);
		}
	};

	const handleSave = async (createTags) => {
		if (validSave()) {
			let productCustomer = Array.isArray(data.productCustomer) ? [...data.productCustomer] : [];
			productCustomer = productCustomer.filter((pc) => !!pc.value);
			productCustomer.forEach((pc, i) => {
				if ((typeof pc.value === "string" || pc.value instanceof String) && !pc.value.includes("_")) {
					productCustomer[i].value = new Date().getTime() + "_" + pc.value;
				}
			});

			data.productCustomer = productCustomer;

			save(
				{ params: { ...data, createTags }, base: base },
				{
					onSuccess: (_response) => {
						addToast("Factura guardada exitosamente", {
							appearance: "success",
						});

						navigate("/facturas");
					},
				},
			);
		}
	};

	const handleConfirm = () => {
		if (isFetchingDetail) return;
		if (missingTags) {
			handleValid();
		} else {
			handleSave(false);
		}
	};

	return (
		<Col md={12} className={classes.col_base}>
			{messagesDelete && Object.keys(messagesDelete).length > 0 && (
				<Col md={12} xs={12}>
					<div>
						{Object.keys(messagesDelete).map((key) => (
							<Alert key={key} variant="danger" className="mb-2">
								{messagesDelete[key]}
							</Alert>
						))}
					</div>
				</Col>
			)}

			{messagesEdit && Object.keys(messagesEdit).length > 0 && (
				<Col md={12} xs={12}>
					<div>
						{Object.keys(messagesEdit).map((key) => (
							<Alert key={key} variant="warning" className="mb-2">
								{messagesEdit[key]}
							</Alert>
						))}
					</div>
				</Col>
			)}

			<Col className={classes.buttons} md={12} xs={12}>
				<div className={classes.buttonClose}>
					<div onClick={() => (navigate(-1) ? navigate(-1) : navigate("/facturas"))}>CERRAR</div>
				</div>

				{isEmpty(messagesDelete) && data && (hasPermission("edit", permissions) || hasPermission("create", permissions)) && (
					<div className={`${classes.buttonSave} ${isFetchingDetail && classes.disabled}`}>
						<div onClick={() => validSave() && setShowPreview(true)}>{isFetchingDetail ? "CARGANDO..." : "GUARDAR"} </div>
					</div>
				)}
			</Col>

			<BasicConfirm
				message={"¿Desea generar etiquetas con los productos ingresados?"}
				subMessage={"Al momento de crear las etiquetas, debe imprimir los códigos generados desde la sección de Inventario."}
				showConfirm={showCreateTags}
				setShowConfirm={setShowCreateTags}
				handleAction={() => handleSave(true)}
				handleActionCancel={() => handleSave(false)}
				textCancel={"NO CREAR ETIQUETAS"}
				textConfirm={"CREAR ETIQUETAS"}
			/>

			<PurchasePreview type={"sale"} show={showPreview} setShow={setShowPreview} data={data} handleConfirm={handleConfirm} />
		</Col>
	);
};

export default CreateOrderSaleButton;
